import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AuthService } from 'app/store/services/auth.service';
import { getIsAuthenticated } from 'app/store/selectors/user.selector';
import { loginFailed } from 'app/store/actions/user.action';
import { IReducers } from 'app/store/reducers';

@Component({
  selector: 'app-google-callback',
  template: '<p>Signing in…</p>',
})
export class GoogleCallbackComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private store: Store<IReducers>,
    private actions$: Actions,
  ) {}

  ngOnInit(): void {
    this.store
      .select(getIsAuthenticated)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAuthenticated: boolean) => {
        if (isAuthenticated) {
          localStorage.setItem('comming_from', 'login');
          this.router.navigate(['/messages']);
        }
      });

    this.actions$
      .pipe(ofType(loginFailed), take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.router.navigate(['/login']);
      });

    const params = this.readCallbackParams();
    const savedState = sessionStorage.getItem('google_sso_state');
    sessionStorage.removeItem('google_sso_state');
    sessionStorage.removeItem('google_sso_nonce');

    if (!params.idToken || !params.state || params.state !== savedState) {
      this.router.navigate(['/login']);
      return;
    }

    this.authService.loginWithSso('google', { id_token: params.idToken });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Google implicit flow returns id_token on the URL fragment
   * (/auth/google/callback#id_token=...&state=...).
   */
  private readCallbackParams(): { idToken: string | null; state: string | null } {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const query = this.route.snapshot.queryParamMap;

    return {
      idToken: hashParams.get('id_token') || query.get('id_token'),
      state: hashParams.get('state') || query.get('state'),
    };
  }
}
